import { useMemo, useState, type KeyboardEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useApproveRange, useSchedule, useSwapDays } from "../api/queries";
import type { ScheduleDay } from "../api/schemas";
import { useCan } from "../auth/AuthContext";
import {
  Button,
  CategoryTag,
  ConfirmDialog,
  ErrorState,
  Field,
  PageHeader,
  Skeleton,
  StatusChip,
  TextInput,
  TierDots,
} from "../components/ui";
import {
  addDays,
  addMonths,
  daysBetween,
  endOfMonth,
  formatLongDate,
  formatMonth,
  formatShortDate,
  startOfMonth,
  todayUtc,
  weekdayMon0,
} from "../lib/dates";
import { typeSlug } from "../lib/format";
import styles from "./pages.module.css";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const CATEGORIES = ["pattern", "number", "spatial", "logic", "memory", "speed", "planning", "word"];

export function SchedulePage() {
  const today = todayUtc();
  const [params, setParams] = useSearchParams();
  const month = startOfMonth(params.get("month") ? `${params.get("month")}-01` : today);
  const gridFrom = addDays(month, -weekdayMon0(month));
  const monthEnd = endOfMonth(month);
  const gridTo = addDays(monthEnd, 6 - weekdayMon0(monthEnd));
  const q = useSchedule(gridFrom, gridTo);
  const canEdit = useCan("schedule.edit");
  const navigate = useNavigate();

  const [selected, setSelected] = useState<string[]>([]);
  const [swapOpen, setSwapOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const swap = useSwapDays();

  const byDate = useMemo(() => new Map((q.data?.days ?? []).map((d) => [d.date, d])), [q.data]);
  const firstSwappable = addDays(today, 2);
  // Past, too soon, published or retired (answers locked), or nothing to move.
  const isLocked = (d: ScheduleDay | undefined, date: string) =>
    date < firstSwappable || d?.status === "published" || d?.status === "retired" || !d?.puzzleId;

  const setMonth = (m: string) => {
    setSelected([]);
    setParams({ month: m.slice(0, 7) }, { replace: true });
  };

  const toggle = (date: string) => {
    setNotice(null);
    setSelected((s) => (s.includes(date) ? s.filter((x) => x !== date) : s.length >= 2 ? [s[1]!, date] : [...s, date]));
  };

  const onGridKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const date = target.dataset.date;
    if (!date) return;
    const delta = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 7, ArrowUp: -7 }[e.key];
    if (!delta) return;
    e.preventDefault();
    const next = addDays(date, delta);
    e.currentTarget.querySelector<HTMLElement>(`[data-date="${next}"]`)?.focus();
  };

  const days = Array.from({ length: daysBetween(gridFrom, gridTo) + 1 }, (_, i) => addDays(gridFrom, i));
  const [a, b] = selected;
  const monthDrafts = (q.data?.days ?? []).filter((d) => d.date >= month && d.date <= monthEnd && d.status === "draft").length;

  return (
    <>
      <PageHeader
        title="Schedule"
        meta={
          <span>
            One puzzle a day. Days from <span className="mono">{formatShortDate(firstSwappable)}</span> that aren't published can be swapped.
          </span>
        }
        actions={
          <>
            <Button size="sm" variant="ghost" onClick={() => setMonth(addMonths(month, -1))} aria-label="Previous month">
              ←
            </Button>
            <span className={styles.monthLabel}>{formatMonth(month)}</span>
            <Button size="sm" variant="ghost" onClick={() => setMonth(addMonths(month, 1))} aria-label="Next month">
              →
            </Button>
            <Button size="sm" onClick={() => setMonth(today)} disabled={month === startOfMonth(today)}>
              This month
            </Button>
            {canEdit ? (
              <Button size="sm" variant="primary" onClick={() => setApproveOpen(true)}>
                Approve range…
              </Button>
            ) : null}
          </>
        }
      />

      <div className={styles.scheduleBar} data-active={selected.length > 0 || undefined}>
        {canEdit ? (
          selected.length === 0 ? (
            <span className={styles.faint}>
              Select two unlocked days to swap them. {monthDrafts ? `${monthDrafts} draft${monthDrafts === 1 ? "" : "s"} still need review this month.` : ""}
            </span>
          ) : (
            <>
              <span className="monoLabel">Swap</span>
              <span className="mono">{a ? formatShortDate(a) : "—"}</span>
              <span className={styles.faint}>↔</span>
              <span className="mono">{b ? formatShortDate(b) : "pick another day"}</span>
              <span className={styles.spacer} />
              <Button size="sm" variant="ghost" onClick={() => setSelected([])}>
                Clear
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={selected.length !== 2}
                onClick={() => {
                  swap.reset();
                  setSwapOpen(true);
                }}
              >
                Swap days
              </Button>
            </>
          )
        ) : (
          <span className={styles.readOnly}>Read-only · swapping and approving need the editor role</span>
        )}
        {notice ? <span className={styles.notice}>{notice}</span> : null}
      </div>

      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <div className={styles.calendar} role="grid" aria-label={`Schedule for ${formatMonth(month)}`} onKeyDown={onGridKey}>
          {WEEKDAYS.map((w) => (
            <div key={w} className={styles.weekday} role="columnheader">
              {w}
            </div>
          ))}
          {days.map((date) => {
            const d = byDate.get(date);
            const outside = date < month || date > monthEnd;
            if (q.isPending) return <Skeleton key={date} height={112} radius="18px" />;
            const locked = isLocked(d, date);
            const isSelected = selected.includes(date);
            const past = date < today;
            return (
              <button
                key={date}
                type="button"
                role="gridcell"
                data-date={date}
                className={styles.day}
                data-cat={d?.category ?? undefined}
                data-outside={outside || undefined}
                data-locked={locked || undefined}
                data-past={past || undefined}
                data-today={date === today || undefined}
                data-selected={isSelected || undefined}
                data-empty={!d?.puzzleId || undefined}
                data-void={!d || undefined}
                aria-pressed={canEdit && !locked ? isSelected : undefined}
                aria-label={`${formatLongDate(date)}${d?.puzzleId ? `, puzzle ${d.puzzleNumber}, ${d.type}, ${d.status}` : d ? ", no puzzle" : ", before launch"}${locked ? ", locked" : ""}`}
                onClick={() => {
                  if (canEdit && !locked) toggle(date);
                  else if (d?.puzzleId) navigate(`/puzzles/${encodeURIComponent(d.puzzleId)}`);
                  else setNotice(d ? `${formatShortDate(date)} has no puzzle scheduled.` : `${formatShortDate(date)} is before puzzle #1.`);
                }}
              >
                <span className={styles.dayTop}>
                  <span className={styles.dayNum}>{Number(date.slice(8))}</span>
                  {d?.puzzleId ? <span className={styles.dayPuzzle}>#{d.puzzleNumber}</span> : null}
                  {locked && d?.puzzleId ? (
                    <span className={styles.lock} aria-hidden title="Locked">
                      ●
                    </span>
                  ) : null}
                </span>
                {d?.puzzleId ? (
                  <>
                    <span className={styles.dayType}>{typeSlug(d.type)}</span>
                    <span className={styles.dayFoot}>
                      <TierDots tier={d.tier} />
                      <StatusChip status={d.status} />
                    </span>
                  </>
                ) : (
                  <span className={styles.dayEmpty}>{d ? "no puzzle" : ""}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className={styles.legend}>
        {CATEGORIES.map((c) => (
          <CategoryTag key={c} category={c} />
        ))}
        <span className={styles.legendNote}>
          <span className={styles.lockSample}>●</span> locked: past, published, or tomorrow
        </span>
      </div>

      <ConfirmDialog
        open={swapOpen}
        title="Swap these two days?"
        confirmLabel="Swap"
        busy={swap.isPending}
        error={swap.error}
        onCancel={() => setSwapOpen(false)}
        onConfirm={() =>
          a &&
          b &&
          swap.mutate(
            { dateA: a, dateB: b },
            {
              onSuccess: () => {
                setSwapOpen(false);
                setSelected([]);
                setNotice(`Swapped ${formatShortDate(a)} and ${formatShortDate(b)}.`);
              },
            },
          )
        }
      >
        {a && b ? (
          <div className={styles.swapPreview}>
            {[a, b].map((date, i) => {
              const d = byDate.get(date);
              const other = byDate.get(i === 0 ? b : a);
              return (
                <div key={date}>
                  <span className="mono">{formatLongDate(date)}</span>
                  <span>
                    <code>{d?.puzzleId}</code> → <code>{other?.puzzleId}</code>
                  </span>
                </div>
              );
            })}
            <p className={styles.faint}>Each puzzle's date and puzzle number are recomputed. Logged in the audit log.</p>
          </div>
        ) : null}
      </ConfirmDialog>

      {approveOpen ? (
        <ApproveRangeDialog
          defaultFrom={month > firstSwappable ? month : firstSwappable}
          defaultTo={monthEnd}
          onClose={() => setApproveOpen(false)}
          onDone={(n) => setNotice(`Approved ${n} draft${n === 1 ? "" : "s"}.`)}
        />
      ) : null}
    </>
  );
}

function ApproveRangeDialog({
  defaultFrom,
  defaultTo,
  onClose,
  onDone,
}: {
  defaultFrom: string;
  defaultTo: string;
  onClose: () => void;
  onDone: (n: number) => void;
}) {
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const preview = useSchedule(from <= to ? from : to, from <= to ? to : from);
  const drafts = (preview.data?.days ?? []).filter((d) => d.status === "draft");
  const approve = useApproveRange();
  const invalid = !from || !to || from > to;
  return (
    <ConfirmDialog
      open
      title="Approve every draft in a range"
      confirmLabel={drafts.length ? `Approve ${drafts.length}` : "Approve"}
      busy={approve.isPending}
      error={approve.error ?? (invalid ? new Error("Pick a start date on or before the end date.") : null)}
      onCancel={onClose}
      onConfirm={() =>
        !invalid &&
        approve.mutate(
          { from, to },
          {
            onSuccess: (r) => {
              onDone(r.approved);
              onClose();
            },
          },
        )
      }
    >
      <div className={styles.rangeFields}>
        <Field label="From">
          <TextInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="To">
          <TextInput type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
      </div>
      <p>
        {preview.isPending ? (
          "Counting drafts…"
        ) : drafts.length ? (
          <>
            <strong>{drafts.length}</strong> draft{drafts.length === 1 ? "" : "s"} will be approved:{" "}
            <span className="mono">{drafts.slice(0, 8).map((d) => `#${d.puzzleNumber}`).join(" ")}</span>
            {drafts.length > 8 ? " …" : ""}
          </>
        ) : (
          "No drafts in this range."
        )}
      </p>
      <p className={styles.faint}>
        Only <code>draft</code> puzzles change. Review them first in <Link to="/puzzles?status=draft">Puzzles</Link>.
      </p>
    </ConfirmDialog>
  );
}
